import React, { useState, useEffect, useContext, useMemo } from 'react';
import CreateExpOptions from './CreateExpOptions';
import { RadioGroups } from 'components/customComponents/Layers/InputGroups';
import WizardService from 'business/services/WizardService';
import useRockFetchGet, { useRockFetchPost } from 'utils/RockFetch';
import WizardHeading from 'components/customComponents/Controls/WizardHeading';
import WizardParagraph from 'components/customComponents/Controls/WizardParagraph';
import ScreenWizardContext from './ScreenWizardContext';
import { isNullOrUndefined } from 'utils/Global';
import DropDownWizardTemplate from 'components/customComponents/Controls/DropDownWizardTemplate';
import { RMModal } from 'components/modal';
import { FmlxDropdown } from 'fmlx-common-ui';
import { PlateTypeDTO } from '../../../business/models/Models';
import './ScreenExperimentWizard.scss';
import ScoreGroupService from 'business/services/PlateService';

function SelectPlateType() {
  const [showDelModal, setShowDelModal] = useState(false);
  const screenWizContext = useContext(ScreenWizardContext);
  const [rockFetchGet] = useRockFetchGet();
  const [rockFetchPost] = useRockFetchPost();
  const [nameValExpTemplates, setnameValExpTemplates] = useState([]);
  const [plateTypes, setPlateTypes] = useState<PlateTypeDTO[]>([]);
  const [recentPlateIds, setRecentPlateIds] = useState<number[]>([]);
  const [delTemplateFileName, setDelTemplateFileName] = useState<string>('');
  function changeCreateNewExp() {
    screenWizContext.setCreateNewExp(!screenWizContext.createNewExp);
  }
  useEffect(() => {
    async function fetchPlates() {
      const service: WizardService = new WizardService();
      const response = await service.getPlateTypes();
      if (response != null) {
        setPlateTypes(response);
      }
    }
    async function fetchRecentPlates() {
      const service: ScoreGroupService = new ScoreGroupService();
      const response = await service.GetUserRecentContainerIds();
      if (response) {
        setRecentPlateIds(response);
      }
    }
    fetchPlates();
    fetchRecentPlates();
  }, []);

  useEffect(() => {
    async function fetchTemplates() {
      const response: string[] = await rockFetchGet(
        '/api/ScreenExpWizard/templatenames',
        'get',
        undefined,
        true,
      );
      if (response != null) {
        screenWizContext.setTemplateNames(response);
      }
    }
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (plateTypes.length > 0 && screenWizContext.plateType == null) {
      screenWizContext.setPlateType(plateTypes[0]);
    }
  }, [plateTypes]);
  function cancelTemplateDelete(val) {
    setShowDelModal(val);
  }
  async function postDeleteTemplate() {
    if (delTemplateFileName.length === 0 || isNullOrUndefined(delTemplateFileName)) {
      return;
    }
    const templateNames = screenWizContext.templateNames;
    const delIndex = templateNames.findIndex(
      (tempName) => tempName === delTemplateFileName,
    );

    if (delIndex != -1) {
      templateNames.splice(delIndex, 1);
      screenWizContext.setTemplateNames([...templateNames]);
      if (templateNames.length == 0) {
        screenWizContext.setCreateNewExp(true);
      }
    }
    const response = await rockFetchPost(
      '/api/screenExpWizard/deleteTemplateName',
      delTemplateFileName,
    );
    setShowDelModal(false);
    if (response != null) {
      return response;
    }
  }

  useEffect(() => {
    const templates = [];
    screenWizContext.templateNames.forEach((val) => {
      templates.push({
        name: val,
        id: val,
      });
    });
    setnameValExpTemplates(templates);
  }, [screenWizContext.templateNames]);

  const plateOptions = useMemo(() => {
    if (!plateTypes) {
      return [];
    }
    const plates = [];
    if (recentPlateIds?.length > 0) {
      for (const recentPlateId of recentPlateIds) {
        const plate = plateTypes.find((pt) => pt.id == recentPlateId);
        if (plate != null) {
          plates.push({
            id: plate.id,
            text: plate.name,
            group: 'Recently Used',
            disabled: false,
          });
        }
      }
    }

    if (plates.length === 0) {
      plates.push({
        id: -1,
        text: (
          <div className="w-full text-center text-sm text-[#888888]">
            No recently used plates
          </div>
        ) as any,
        group: 'Recently Used',
        disabled: true,
      });
    }

    const allPlates = plateTypes.sort((a, b) => a.name.localeCompare(b.name));
    allPlates.forEach((ap) => {
      plates.push({
        id: ap.id,
        text: ap.name,
        group: 'All Plates',
        disabled: false,
      });
    });
    return plates;
  }, [plateTypes, recentPlateIds]);

  function changeExpTemplate(val) {
    screenWizContext.setExpTemplate(val);
  }

  function onChange(e) {
    const pTypeId = parseInt(e);
    const activePlate = plateTypes.find((platetype) => platetype.id === pTypeId);
    if (!isNullOrUndefined(activePlate)) {
      screenWizContext.setPlateType(activePlate);
      const proteins = [];
      for (let index = 0; index < activePlate.totalDrops; index++) {
        proteins.push(null);
      }
      screenWizContext.changeProteins([...proteins]);
    }
  }
  function deleteTemplateName(fileName: string) {
    setShowDelModal(true);
    if (!isNullOrUndefined(fileName)) {
      setDelTemplateFileName(fileName);
    }
  }
  return (
    <>
      <RMModal
        id="selectplatetype-modal"
        showModal={showDelModal}
        setShowModal={cancelTemplateDelete}
        okButtonText="Yes, Proceed"
        noButtonText="No, Cancel"
        body="You’re about to remove 1 item. Are you sure you want to continue?"
        onOk={postDeleteTemplate}
        heading="Remove Item(s)"
      />
      <div id="selectplatetype-wrapper" className="flex-y-center flex-column">
        <WizardHeading text="Welcome to Screen Experiment Wizard" />
        <WizardParagraph
          text="This wizard helps you create several screen experiments at once. In the
        steps that follow you will select the plate type, screens you want to
        use and protein. You will also set the imaging schedule, the experiment
        setup and incubation temperatures, and select an experiment owner."
        />
        <CreateExpOptions
          heading="Create Experiment Options"
          selectedOption={screenWizContext.expTemplate}
          setSelectedOption={changeExpTemplate}
          options={nameValExpTemplates}
          disableSelect={screenWizContext.createNewExp}
        >
          <RadioGroups
            label1="Create New Screen Experiment"
            label2="Create Screen Experiment Based on Saved Wizard Setup"
            radioValue={screenWizContext.createNewExp}
            onChange={changeCreateNewExp}
            disabledSecond={screenWizContext.templateNames.length === 0}
          />
          <DropDownWizardTemplate
            onChange={changeExpTemplate}
            disabled={screenWizContext.createNewExp}
            showTemplateNameModal={deleteTemplateName}
          />
        </CreateExpOptions>
        <CreateExpOptions
          heading="Select Plate Type"
          selectedOption={screenWizContext.plateType?.id}
          setSelectedOption={onChange}
          options={plateTypes}
        >
          <div className="plateTypeDropdown">
            <FmlxDropdown
              id={'selectplatetype-fmlxdropdown'}
              items={plateOptions}
              menuPosition={FmlxDropdown.MenuPosition.TOP}
              placeholder="Select Plate Type"
              selectedItem={screenWizContext.plateType?.id}
              onChange={onChange}
              separator
              menuProps={
                {
                  classes: { paper: 'selectPlateTypeDropdown' },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                  MenuListProps: {
                    style: {
                      maxHeight: '27rem', // 11.5 items (1 item is ~2 rem, 1 partially cut off) + 1 header (~4 rem)
                    },
                  },
                } as any
              }
            />
          </div>
        </CreateExpOptions>
      </div>
    </>
  );
}

export default SelectPlateType;
